'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { INK, SURFACE } from './ConversationList';
import { haptic } from './haptics';

const TRIGGER = 64;
const MAX = 104;
const HOLD = 52;

type Props = {
  onRefresh: () => Promise<unknown> | void;
  children: ReactNode;
};

export default function PullToRefresh({ onRefresh, children }: Props) {
  const scroller = useRef<HTMLDivElement>(null);
  const refreshRef = useRef(onRefresh);
  useEffect(() => {
    refreshRef.current = onRefresh;
  }, [onRefresh]);
  const gesture = useRef({ x: 0, y: 0, active: false, engaged: false, pull: 0, busy: false, armed: false });
  const [pull, setPull] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const g = gesture.current;

    const start = (event: TouchEvent) => {
      g.active = !g.busy && el.scrollTop <= 0 && event.touches.length === 1;
      g.engaged = false;
      g.armed = false;
      g.pull = 0;
      g.x = event.touches[0].clientX;
      g.y = event.touches[0].clientY;
    };

    const move = (event: TouchEvent) => {
      if (!g.active) return;
      const dx = event.touches[0].clientX - g.x;
      const dy = event.touches[0].clientY - g.y;
      if (!g.engaged) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (dy <= 0 || Math.abs(dx) > dy || el.scrollTop > 0) {
          g.active = false;
          return;
        }
        g.engaged = true;
        setDragging(true);
      }
      if (event.cancelable) event.preventDefault();
      g.pull = Math.min(MAX, Math.max(0, dy) * 0.5);
      setPull(g.pull);
      const armed = g.pull >= TRIGGER;
      if (armed !== g.armed) haptic();
      g.armed = armed;
    };

    const end = async () => {
      if (!g.active || !g.engaged) {
        g.active = false;
        return;
      }
      g.active = false;
      setDragging(false);
      if (g.pull < TRIGGER) {
        setPull(0);
        return;
      }
      g.busy = true;
      setRefreshing(true);
      setPull(HOLD);
      try {
        await refreshRef.current();
      } finally {
        g.busy = false;
        setRefreshing(false);
        setPull(0);
      }
    };

    el.addEventListener('touchstart', start, { passive: true });
    el.addEventListener('touchmove', move, { passive: false });
    el.addEventListener('touchend', end);
    el.addEventListener('touchcancel', end);
    return () => {
      el.removeEventListener('touchstart', start);
      el.removeEventListener('touchmove', move);
      el.removeEventListener('touchend', end);
      el.removeEventListener('touchcancel', end);
    };
  }, []);

  const progress = Math.min(1, pull / TRIGGER);
  const ease = dragging ? 'none' : 'transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 200ms ease';

  return (
    <Box ref={scroller} data-scroller sx={{ position: 'relative', flex: 1, overflowY: 'auto', overscrollBehaviorY: 'contain' }}>
      <Box
        aria-hidden={!refreshing}
        role={refreshing ? 'status' : undefined}
        aria-label={refreshing ? 'Refreshing' : undefined}
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          zIndex: 1,
          width: 32,
          height: 32,
          ml: '-16px',
          display: 'grid',
          placeItems: 'center',
          borderRadius: '50%',
          bgcolor: SURFACE,
          boxShadow: '0 2px 8px rgba(20,20,18,0.12), 0 0 0 0.5px rgba(20,20,18,0.08)',
          opacity: refreshing ? 1 : progress,
          transform: `translateY(${pull - 40}px) rotate(${refreshing ? 0 : progress * 270}deg)`,
          transition: ease,
          pointerEvents: 'none',
        }}
      >
        <CircularProgress
          size={18}
          thickness={5}
          variant={refreshing ? 'indeterminate' : 'determinate'}
          value={refreshing ? undefined : progress * 85}
          sx={{ color: INK, opacity: refreshing || progress >= 1 ? 1 : 0.55 }}
        />
      </Box>
      <Box sx={{ transform: pull ? `translateY(${pull}px)` : 'none', transition: ease, pb: 'env(safe-area-inset-bottom)' }}>{children}</Box>
    </Box>
  );
}
